import { AlertDialog as AlertPrimitive, Dialog as DialogPrimitive } from 'radix-ui';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { cn } from '../cn';
import { CloseIcon } from '../icons';
import { Button } from './button';

export const Dialog = DialogPrimitive.Root;
export const DialogTrigger = DialogPrimitive.Trigger;
export const DialogClose = DialogPrimitive.Close;

const overlay = 'fixed inset-0 z-(--z-dialog) bg-scrim data-[state=open]:animate-fade-in';

export interface DialogContentProps {
  title: ReactNode;
  description?: ReactNode;
  children?: ReactNode;
  footer?: ReactNode;
  /** confirm: 480px. form: 640px. */
  size?: 'confirm' | 'form';
  className?: string;
  onOpenAutoFocus?: (e: Event) => void;
}

/** Modal for confirmations and one-decision forms (1–3 fields). Longer work belongs in a Drawer or a page. */
export function DialogContent({ title, description, children, footer, size = 'confirm', className, onOpenAutoFocus }: DialogContentProps) {
  const { t } = useTranslation();
  return (
    <DialogPrimitive.Portal>
      <DialogPrimitive.Overlay className={overlay} />
      <DialogPrimitive.Content
        onOpenAutoFocus={onOpenAutoFocus}
        className={cn(
          'fixed start-1/2 top-[12vh] z-(--z-dialog) flex max-h-[76vh] w-[calc(100vw-32px)] -translate-x-1/2 flex-col rounded-md border border-border bg-surface shadow-overlay outline-none data-[state=open]:animate-fade-in rtl:translate-x-1/2',
          size === 'confirm' ? 'max-w-dialog' : 'max-w-dialog-form',
          className,
        )}
      >
        <div className="flex items-start justify-between gap-4 border-b border-border px-5 py-3.5">
          <div className="flex flex-col gap-0.5">
            <DialogPrimitive.Title className="text-h2 text-ink">{title}</DialogPrimitive.Title>
            {description ? <DialogPrimitive.Description className="text-body text-ink-secondary">{description}</DialogPrimitive.Description> : null}
          </div>
          <DialogPrimitive.Close asChild>
            <Button variant="ghost" size="sm" className="-me-2 w-7 px-0" aria-label={t('actions.close')}>
              <CloseIcon />
            </Button>
          </DialogPrimitive.Close>
        </div>
        {children ? <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-5 py-4">{children}</div> : null}
        {footer ? <div className="flex items-center justify-end gap-2 border-t border-border px-5 py-3">{footer}</div> : null}
      </DialogPrimitive.Content>
    </DialogPrimitive.Portal>
  );
}

export const AlertDialog = AlertPrimitive.Root;
export const AlertDialogTrigger = AlertPrimitive.Trigger;

export interface AlertDialogContentProps {
  title: ReactNode;
  description: ReactNode;
  children?: ReactNode;
  confirmLabel: ReactNode;
  cancelLabel?: ReactNode;
  onConfirm: () => void;
  tone?: 'default' | 'danger';
  loading?: boolean;
}

/** Confirmation for irreversible or consequential actions. States the consequence in plain words. */
export function AlertDialogContent({ title, description, children, confirmLabel, cancelLabel, onConfirm, tone = 'default', loading }: AlertDialogContentProps) {
  const { t } = useTranslation();
  return (
    <AlertPrimitive.Portal>
      <AlertPrimitive.Overlay className={overlay} />
      <AlertPrimitive.Content className="fixed start-1/2 top-[18vh] z-(--z-dialog) flex w-[calc(100vw-32px)] max-w-dialog -translate-x-1/2 flex-col rounded-md border border-border bg-surface shadow-overlay outline-none data-[state=open]:animate-fade-in rtl:translate-x-1/2">
        <div className="flex flex-col gap-1.5 px-5 pt-4 pb-3">
          <AlertPrimitive.Title className="text-h2 text-ink">{title}</AlertPrimitive.Title>
          <AlertPrimitive.Description className="text-body text-ink-secondary">{description}</AlertPrimitive.Description>
        </div>
        {children ? <div className="px-5 pb-3">{children}</div> : null}
        <div className="flex items-center justify-end gap-2 border-t border-border px-5 py-3">
          <AlertPrimitive.Cancel asChild>
            <Button variant="secondary">{cancelLabel ?? t('actions.cancel')}</Button>
          </AlertPrimitive.Cancel>
          <Button variant={tone === 'danger' ? 'danger-solid' : 'primary'} loading={loading} onClick={onConfirm}>
            {confirmLabel}
          </Button>
        </div>
      </AlertPrimitive.Content>
    </AlertPrimitive.Portal>
  );
}
